import { type AppDispatch } from 'common/hooks/redux'
import { mergeDefined } from 'common/utils/mergeDefined'
import dayjs from 'dayjs'
import { type IPatientStatus } from 'features/Home/interfaces/IInfoSchedule'
import { type PatientAppointmentType } from 'features/Schedule/pusher/type'
import _ from 'lodash'
import { scheduleApi } from './schedule.api'

export const updateRoomStatusOptimistic = (
  dispatch: AppDispatch,
  siteId: string,
  date: string,
  room_id: number,
  room_status_code: number,
) => {
  dispatch(
    scheduleApi.util.updateQueryData('fetchScheduleDay', { siteId, date }, (draft) => {
      if (!draft || !draft.rooms) return

      const roomIndex = draft.rooms.findIndex((room) => room.id === room_id)
      if (roomIndex !== -1) {
        const status = draft.room_statuses.find((status) => status.code === room_status_code)
        draft.rooms[roomIndex] = {
          ...draft.rooms[roomIndex],
          status,
        }
      }
    }) as any,
  )
}

export const updatePatientOptimistic = (
  dispatch: AppDispatch,
  siteId: string,
  date: string,
  patientData: PatientAppointmentType,
) => {
  const currentUrlDate = window.location.pathname.split('/').pop() || date
  const isDateChanged = currentUrlDate !== patientData.date

  if (isDateChanged) {
    return deletePatientOptimistic(dispatch, siteId, currentUrlDate, patientData.id)
  }

  dispatch(
    scheduleApi.util.updateQueryData('fetchScheduleDay', { siteId, date }, (draft) => {
      if (!draft || !draft.items) return
      const room = draft.rooms.find((room) => room.value === patientData.room.code)
      const originalIndex = draft.items.findIndex((item) => item.id === patientData.id)

      if (originalIndex !== -1) {
        const existing = draft.items[originalIndex]
        const newObj = {
          ...mergeDefined(
            existing as Record<string, unknown>,
            patientData as Record<string, unknown>,
          ),
          room: room ?? existing.room,
        } as typeof existing
        const isDeepEqual = _.isEqual(draft.items[originalIndex], newObj)

        if (!isDeepEqual) {
          draft.items.splice(originalIndex, 1, newObj)
        }
      }
    }) as any,
  )
}

export const deletePatientOptimistic = (
  dispatch: AppDispatch,
  siteId: string,
  date: string,
  appointment_id: number,
) => {
  dispatch(
    scheduleApi.util.updateQueryData('fetchScheduleDay', { siteId, date }, (draft) => {
      if (!draft || !draft.items) return
      const originalIndex = draft.items.findIndex((item) => item.id === appointment_id)
      if (originalIndex !== -1) {
        draft.items.splice(originalIndex, 1)
      }
    }) as any,
  )
}
export const createPatientOptimistic = (
  dispatch: AppDispatch,
  siteId: string,
  date: string,
  patientData: PatientAppointmentType,
) => {
  dispatch(
    scheduleApi.util.updateQueryData('fetchScheduleDay', { siteId, date }, (draft) => {
      if (!draft || !draft.items) return

      const room = draft.rooms.find((room) => room.value === patientData.room.code)
      const originalIndex = draft.items.findIndex((item) => item.id === patientData.id)

      if (originalIndex !== -1) {
        const existing = draft.items[originalIndex]
        const newObj = {
          ...mergeDefined(
            existing as Record<string, unknown>,
            patientData as Record<string, unknown>,
          ),
          room: room ?? existing.room,
        } as typeof existing

        if (!_.isEqual(draft.items[originalIndex], newObj)) {
          draft.items.splice(originalIndex, 1, newObj)
        }
        return
      }

      draft.items.push({
        ...patientData,
        room,
      })
      if (!patientData.emr_updated_at) {
        draft.has_ehr_booking = true
      }
    }) as any,
  )
}

export const updatePatientStatusOptimistic = (
  dispatch: AppDispatch,
  siteId: string,
  date: string,
  appointment_id: number,
  status_code: number,
  temperature: number,
) => {
  dispatch(
    scheduleApi.util.updateQueryData('fetchScheduleDay', { siteId, date }, (draft) => {
      if (!draft || !draft.items) return

      const status = draft.patient_statuses.find((status) => status.code === status_code)!
      const originalIndex = draft.items.findIndex((item) => item.id === appointment_id)
      if (originalIndex !== -1) {
        draft.items[originalIndex] = {
          ...draft.items[originalIndex],
          status: status as IPatientStatus,
          temperature,
        }
      }
    }) as any,
  )
}

export const updatePatientLocationOptimistic = (
  dispatch: AppDispatch,
  siteId: string,
  date: string,
  appointment_id: number,
  room_id: number,
) => {
  dispatch(
    scheduleApi.util.updateQueryData('fetchScheduleDay', { siteId, date }, (draft) => {
      if (!draft || !draft.items) return

      const room = draft.rooms.find((room) => room.id === room_id)
      const originalIndex = draft.items.findIndex((item) => item.id === appointment_id)
      if (originalIndex !== -1) {
        draft.items[originalIndex] = {
          ...draft.items[originalIndex],
          room,
        }
      }
    }) as any,
  )
}

export const updatePatientOptimisticWeek = (
  dispatch: AppDispatch,
  siteId: string,
  startDate: string,
  endDate: string,
  patientData: PatientAppointmentType,
) => {
  dispatch(
    scheduleApi.util.updateQueryData(
      'fetchScheduleWeek',
      { siteId, startDate, endDate },
      (draft) => {
        if (!draft || !draft.items) return

        const room = draft.rooms.find((r) => r.value === patientData.room.code)

        const originalIndex = draft.items.findIndex((item) => item.id === patientData.id)

        if (originalIndex !== -1) {
          const existing = draft.items[originalIndex]
          const newObj = {
            ...mergeDefined(
              existing as Record<string, unknown>,
              patientData as Record<string, unknown>,
            ),
            room: room ?? existing.room,
          } as typeof existing
          const isDeepEqual = _.isEqual(draft.items[originalIndex], newObj)

          if (!isDeepEqual) {
            draft.items.splice(originalIndex, 1, newObj)
          }
        }
      },
    ) as any,
  )
}

export const deletePatientOptimisticWeek = (
  dispatch: AppDispatch,
  siteId: string,
  startDate: string,
  endDate: string,
  appointment_id: number,
) => {
  dispatch(
    scheduleApi.util.updateQueryData(
      'fetchScheduleWeek',
      { siteId, startDate, endDate },
      (draft) => {
        if (!draft || !draft.items) return
        const originalIndex = draft.items.findIndex((item) => item.id === appointment_id)
        if (originalIndex !== -1) {
          draft.items.splice(originalIndex, 1)
        }
      },
    ) as any,
  )
}

export const createPatientOptimisticWeek = (
  dispatch: AppDispatch,
  siteId: string,
  startDate: string,
  endDate: string,
  patientData: PatientAppointmentType,
) => {
  dispatch(
    scheduleApi.util.updateQueryData(
      'fetchScheduleWeek',
      { siteId, startDate, endDate },
      (draft) => {
        if (!draft || !draft.items) return

        const room = draft.rooms.find((room) => room.value === patientData.room.code)
        const originalIndex = draft.items.findIndex((item) => item.id === patientData.id)

        if (originalIndex !== -1) {
          const existing = draft.items[originalIndex]
          const newObj = {
            ...mergeDefined(
              existing as Record<string, unknown>,
              patientData as Record<string, unknown>,
            ),
            room: room ?? existing.room,
          } as typeof existing

          if (!_.isEqual(draft.items[originalIndex], newObj)) {
            draft.items.splice(originalIndex, 1, newObj)
          }
          return
        }

        draft.items.push({
          ...patientData,
          room,
        })
      },
    ) as any,
  )
}

export const updatePatientStatusOptimisticWeek = (
  dispatch: AppDispatch,
  siteId: string,
  startDate: string,
  endDate: string,
  appointment_id: number,
  status_code: number,
  temperature: number,
) => {
  dispatch(
    scheduleApi.util.updateQueryData(
      'fetchScheduleWeek',
      { siteId, startDate, endDate },
      (draft) => {
        if (!draft || !draft.items) return

        const status = draft.patient_statuses.find((status) => status.code === status_code)!
        const originalIndex = draft.items.findIndex((item) => item.id === appointment_id)
        if (originalIndex !== -1) {
          draft.items[originalIndex] = {
            ...draft.items[originalIndex],
            status: status as IPatientStatus,
            temperature,
          }
        }
      },
    ) as any,
  )
}

export const updatePatientLocationOptimisticWeek = (
  dispatch: AppDispatch,
  siteId: string,
  startDate: string,
  endDate: string,
  appointment_id: number,
  room_id: number,
) => {
  dispatch(
    scheduleApi.util.updateQueryData(
      'fetchScheduleWeek',
      { siteId, startDate, endDate },
      (draft) => {
        if (!draft || !draft.items) return

        const room = draft.rooms.find((room) => room.id === room_id)
        const originalIndex = draft.items.findIndex((item) => item.id === appointment_id)
        if (originalIndex !== -1) {
          draft.items[originalIndex] = {
            ...draft.items[originalIndex],
            room,
          }
        }
      },
    ) as any,
  )
}

export const getActiveWeekQueries = (
  queries: any,
  siteId: string,
  date: string,
): Array<{ startDate: string; endDate: string }> => {
  const targetDate = dayjs(date)
  const activeWeekQueries: Array<{ startDate: string; endDate: string }> = []

  Object.keys(queries).forEach((queryKey) => {
    if (queryKey.startsWith('fetchScheduleWeek')) {
      const query = queries[queryKey]
      if (query?.originalArgs?.siteId === siteId) {
        const startDate = dayjs(query.originalArgs.startDate)
        const endDate = dayjs(query.originalArgs.endDate)

        if (targetDate.isSameOrAfter(startDate) && targetDate.isSameOrBefore(endDate)) {
          activeWeekQueries.push({
            startDate: query.originalArgs.startDate,
            endDate: query.originalArgs.endDate,
          })
        }
      }
    }
  })

  return activeWeekQueries
}
